import React, { useState } from 'react';
import api from '../services/api.ts';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { VesselsAPI, Vessel, VesselStatus } from '../services/vessels.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus } from '../utils/statusFormatter.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import DetailShell from '../components/ui/DetailShell.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import ImageGallery from '../components/ImageGallery.tsx';
import { formatDateOnly } from '../utils/date.ts';
import { vesselCategoryLabel, assetTypeLabel } from '../utils/labels.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { DocumentTextIcon, PhotoIcon } from '@heroicons/react/24/outline';

const getBackendUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const toneFor = (s: string): 'green' | 'amber' | 'slate' | 'blue' | 'red' => {
  if (s === 'in_stock' || s === 'active') return 'green';
  if (s === 'reserved' || s === 'maintenance') return 'amber';
  if (s === 'sold') return 'slate';
  if (s === 'exchanged') return 'blue';
  return 'slate';
};

const VesselDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'related' | 'attachments'>('details');
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  const { data: vessel, isLoading, error } = useQuery<Vessel | null>(
    ['vessel', id],
    async () => {
      if (!id) return null;
      const response = await VesselsAPI.get(Number(id));
      if (response.attachments && Array.isArray(response.attachments)) {
        response.attachments = response.attachments.map((attachment: any) => {
          const type =
            attachment.type ||
            (attachment.mimetype?.startsWith('image/') ? 'image' : 'document');
          return {
            ...attachment,
            type,
            preview: type === 'image' && attachment.url ? getBackendUrl(attachment.url) : undefined,
            file: new File([], attachment.name || 'file'),
          };
        });
      } else {
        response.attachments = [];
      }
      return response;
    }
  );

  const { data: relatedAssets } = useQuery<any[]>(
    ['related-vessel', id],
    async () => {
      if (!id) return [];
      const response = await api.get(`/vessels/${id}/related`);
      return response.data;
    },
    { enabled: !!id }
  );

  const { data: assetRelatedAssets } = useQuery<any[]>(
    ['related-assets', vessel?.assetId],
    async () => {
      if (!vessel?.assetId) return [];
      const response = await api.get(`/assets/${vessel.assetId}/related`);
      return response.data;
    },
    { enabled: !!vessel?.assetId }
  );

  const deleteMutation = useMutation(async () => VesselsAPI.delete(Number(id)), {
    onSuccess: () => {
      queryClient.invalidateQueries(['vessels']);
      navigate('/vessels');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error || !vessel) {
    return <div className="field-error">Embarcação não encontrada.</div>;
  }

  const related =
    relatedAssets && relatedAssets.length > 0 ? relatedAssets : assetRelatedAssets || [];
  const inStock = vessel.status === VesselStatus.IN_STOCK || vessel.status === 'in_stock';

  return (
    <DetailShell
      breadcrumb={`Patrimônio / Embarcações / ${vessel.make} ${vessel.model}`}
      title={`${vessel.make} ${vessel.model}`}
      subtitle={`Registro ${vessel.registrationNumber || '—'}`}
      statusLabel={formatStatus(String(vessel.status))}
      statusTone={toneFor(String(vessel.status))}
      backLabel="Voltar às embarcações"
      onBack={() => navigate('/vessels')}
      actions={
        <>
          {hasPermission('assets.update') && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/vessels/${id}/editar`)}
            >
              Editar
            </button>
          )}
          {inStock && hasPermission('assets.update') && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate(`/vessels/${id}/sale`)}
            >
              Registrar venda
            </button>
          )}
          {hasPermission('assets.delete') && (
            <button type="button" className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
              Excluir
            </button>
          )}
        </>
      }
      tabs={
        <div className="tabs" style={{ marginBottom: 12 }}>
          {(
            [
              ['details', 'Resumo'],
              ['related', 'Relacionados'],
              ['attachments', 'Anexos'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`tab${activeTab === key ? ' active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      }
    >
      {activeTab === 'details' && (
        <>
          <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="kpi">
              <div className="kpi-label">Valor de compra</div>
              <div className="kpi-value">{formatCurrency(Number(vessel.purchasePrice || 0))}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Valor de venda</div>
              <div className="kpi-value">
                {vessel.sellingPrice ? formatCurrency(Number(vessel.sellingPrice)) : '—'}
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Categoria</div>
              <div className="kpi-value" style={{ fontSize: 18 }}>
                {vesselCategoryLabel(vessel.category)}
              </div>
            </div>
          </div>

          <div className="card invest-detail-card" style={{ marginBottom: 12 }}>
            <div className="card-header">
              <div className="card-title">Dados da embarcação</div>
              {hasPermission('assets.update') && (
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => navigate(`/vessels/${id}/editar`)}
                >
                  Editar
                </button>
              )}
            </div>
            <dl className="detail-fields">
              <div>
                <dt>Marca</dt>
                <dd>{vessel.make}</dd>
              </div>
              <div>
                <dt>Modelo</dt>
                <dd>{vessel.model}</dd>
              </div>
              <div>
                <dt>Registro</dt>
                <dd className="mono">{vessel.registrationNumber || '—'}</dd>
              </div>
              <div>
                <dt>Categoria</dt>
                <dd>{vesselCategoryLabel(vessel.category)}</dd>
              </div>
              <div>
                <dt>Ano · Cor</dt>
                <dd>
                  {vessel.year} · {vessel.color || '—'}
                </dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <StatusChip
                    label={formatStatus(String(vessel.status))}
                    tone={toneFor(String(vessel.status))}
                  />
                </dd>
              </div>
              <div>
                <dt>Data de compra</dt>
                <dd className="mono">
                  {vessel.purchaseDate ? formatDateOnly(vessel.purchaseDate) : '—'}
                </dd>
              </div>
              <div>
                <dt>Data de venda</dt>
                <dd className="mono">
                  {vessel.soldDate ? formatDateOnly(vessel.soldDate) : '—'}
                </dd>
              </div>
              <div>
                <dt>Valor de compra</dt>
                <dd>{formatCurrency(Number(vessel.purchasePrice || 0))}</dd>
              </div>
              <div>
                <dt>Valor de venda</dt>
                <dd>
                  {vessel.sellingPrice ? formatCurrency(Number(vessel.sellingPrice)) : '—'}
                </dd>
              </div>
            </dl>
          </div>

          {inStock && hasPermission('assets.update') && (
            <div className="toolbar" style={{ marginBottom: 12 }}>
              <div className="filters" />
              <div className="detail-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    navigate(`/exchanges/new?originType=vessel&originId=${id}`)
                  }
                >
                  Registrar permuta
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'attachments' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Anexos</div>
            <button
              type="button"
              className="link-btn"
              onClick={() => navigate(`/vessels/${id}/editar`)}
            >
              Adicionar / editar
            </button>
          </div>
          <div style={{ padding: 20 }}>
            <h4 className="muted" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <PhotoIcon style={{ width: 16, height: 16 }} /> Fotos
            </h4>
            {(vessel.attachments || []).filter((a: any) => a.type === 'image').length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 10, marginBottom: 24 }}>
                {(vessel.attachments || [])
                  .filter((a: any) => a.type === 'image')
                  .map((photo: any, index: number) => (
                    <button
                      key={photo.id}
                      type="button"
                      onClick={() => {
                        setGalleryStartIndex(index);
                        setShowImageGallery(true);
                      }}
                      style={{
                        aspectRatio: '1',
                        borderRadius: 10,
                        overflow: 'hidden',
                        border: '1px solid #e2e8f0',
                        padding: 0,
                        cursor: 'pointer',
                        background: '#f8fafc',
                      }}
                    >
                      <img
                        src={photo.preview || (photo.url ? getBackendUrl(photo.url) : '')}
                        alt={photo.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </button>
                  ))}
              </div>
            ) : (
              <p className="muted" style={{ marginBottom: 24 }}>Nenhuma foto adicionada.</p>
            )}

            <h4 className="muted" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <DocumentTextIcon style={{ width: 16, height: 16 }} /> Documentos
            </h4>
            {(vessel.attachments || []).filter((a: any) => a.type === 'document').length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Arquivo</th>
                      <th className="hide-m">Tamanho</th>
                      <th style={{ textAlign: 'right' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(vessel.attachments || [])
                      .filter((a: any) => a.type === 'document')
                      .map((doc: any) => (
                        <tr key={doc.id}>
                          <td>{doc.name}</td>
                          <td className="hide-m mono">
                            {doc.size ? `${(doc.size / 1024).toFixed(0)} KB` : '—'}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              type="button"
                              className="link-btn"
                              onClick={() => {
                                if (!doc.url) return;
                                const link = document.createElement('a');
                                link.href = getBackendUrl(doc.url);
                                link.download = doc.name;
                                link.target = '_blank';
                                link.click();
                              }}
                            >
                              Baixar
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted">Nenhum documento adicionado.</p>
            )}
          </div>
          {showImageGallery && (
            <ImageGallery
              images={(vessel.attachments || []).filter((a: any) => a.type === 'image')}
              onClose={() => setShowImageGallery(false)}
              initialIndex={galleryStartIndex}
            />
          )}
        </div>
      )}

      {activeTab === 'related' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Ativos relacionados</div>
            <span className="muted">{related.length} registros</span>
          </div>
          {related.length === 0 ? (
            <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
              Nenhum ativo relacionado encontrado.
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ativo</th>
                    <th className="hide-m">Tipo</th>
                    <th className="hide-m">Status</th>
                    <th className="hide-m" style={{ textAlign: 'right' }}>Valor</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {related.map((r: any) => (
                    <tr key={r.id}>
                      <td>{r.description}</td>
                      <td className="hide-m">{assetTypeLabel(r.type)}</td>
                      <td className="hide-m">
                        <StatusChip
                          label={formatStatus(r.status)}
                          tone={toneFor(String(r.status))}
                        />
                      </td>
                      <td className="amount hide-m" style={{ textAlign: 'right' }}>
                        {formatCurrency(Number(r.currentValue || 0))}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => {
                            if (r.type === 'vehicle') navigate(`/vehicles/${r.id}`);
                            else if (r.type === 'aircraft') navigate(`/aircrafts/${r.id}`);
                            else if (r.type === 'vessel') navigate(`/vessels/${r.id}`);
                            else navigate(`/assets/${r.id}`);
                          }}
                        >
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir embarcação"
        message="Tem certeza que deseja excluir esta embarcação? Esta ação não pode ser desfeita."
        onConfirm={() => {
          deleteMutation.mutate();
          setShowDeleteModal(false);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleteMutation.isLoading}
      />
    </DetailShell>
  );
};

export default VesselDetail;
